import _ from "lodash";
import moment from "moment";

import api from "CommonCore/api";
import {
  ABSCHLUSSART,
  ASTMODUSKEY,
  getModusFromAntrag,
  HOCHSCHULE,
} from "App/constants";
import {
  setupLookups,
} from "App/shk-form-tab";


const istKeinTarifvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : antrag && getModusFromAntrag(antrag)) !== ASTMODUSKEY.Tarif,
};

// Die Entity, welche die Abschlussart beinhaltet, kommt je nach Rolle aus der Tabelle  person_werdegang oder aus antrag_werdegang:
const getAbschlussArt = entity => entity.pwg_pfaid !== undefined ? entity.pwg_pfaid : entity.awg_pfaid;
const getHochschule = entity => entity.pwg_khsid !== undefined ? entity.pwg_khsid : entity.awg_khsid;

// Da das Setzen von moment.locale nicht funktioniert, hier die Monatsnamen von Hand.
/* eslint-disable-next-line */
const deutscheMonatsnamen = ["Januar", "Februar", "März", "April", "Mai", "Juni","Juli", "August", "September", "Oktober", "November", "Dezember"];

export const schema = {
  displayName: "Person Werdegang", // Block 1: Werdegang
  table: [
    {
      field: "info_abschluss",
      display: "Geben Sie bitte Ihren höchsten Hochschulabschluss an. Bei mehreren höchsten Abschlüssen können Sie diese im Personalbogen (Anlagen) aufführen.",
      type: "plaintext",
      useHeader: false,
      subtag: "werdegang",
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
    },
    {
      field: "pwg_pfaid",
      display: "Hochschulabschluss",
      initValue: ABSCHLUSSART.OHNE_ABSCHLUSS, // initValue, weil default auch im Readonly-Modus eingefügt wird
      sortable: true,
      subtag: "werdegang",

      type: "select",
      choices: lookup => _.map(_.filter(lookup, l => l.pfa_anzeige == true), l => ({ value: l.pfa_id, label: l.pfa_kbez })),
      inject: ["k_pfaartLookup"],
      selector: lookup => id => _.get(lookup, [id, "pfa_kbez"], ""),
      dependends: [
        {
          fields: ["pwg_khsid", "pwg_datum", "pwg_ksbid", "pwg_kfgid"],
          invisibleIf: value => {
            return !value || value == ABSCHLUSSART.OHNE_ABSCHLUSS;
          }, // ohne Abschluss
          noContainer: true, // if this is false, dependends do not seem to work anymore (PBP-2535)
        },
      ],
      required: true,
      validate: value => {
        if (_.isEmpty(value)) {
          return "Bitte geben Sie den Hochschulabschluss ein";
        }
      },
    },
    {
      field: "pwg_khsid",
      display: "Hochschule",
      sortable: true,
      subtag: "werdegang",
      type: "select",
      choices: lookup => _.map(_.filter(lookup, l => l.khs_anzeige == true), l => ({ value: l.khs_id, label: l.khs_kbez })),
      inject: ["k_hochschuleLookup"],
      selector: lookup => id => _.get(lookup, [id, "khs_kbez"], ""),
      required: true,
      validate: (value, entity) => {
        if (getAbschlussArt(entity) != ABSCHLUSSART.OHNE_ABSCHLUSS
          && _.isEmpty(value)) {
          return "Bitte geben Sie die Hochschule ein";
        }
      },
    },
    {
      field: "pwg_ksaid",
      display: "Staat (bei ausl. (Aus-)Bildungseinrichtung)",
      sortable: true,
      subtag: "werdegang",
      type: "select",
      inject: ["k_staatLookup"],
      noSort: true,
      choices: lookup => lookup && _.map(lookup, staat => ({ value: staat.ksa_id, label: staat.ksa_kbez }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      selector: lookup => id => lookup && id && lookup[id].ksa_kbez,
      required: true,
      validate: (value, entity) => {
        if (getHochschule(entity) == HOCHSCHULE.HS_IM_AUSLAND
          && _.isEmpty(value)) {
          return "Bitte geben Sie den Staat ein";
        }
      },
      hideFromDetail: entity => getHochschule(entity) != HOCHSCHULE.HS_IM_AUSLAND,
      hideFromEditor: entity => getHochschule(entity) != HOCHSCHULE.HS_IM_AUSLAND,
    },
    {
      field: "pwg_datum",
      display: "Datum des Abschlusses",
      type: "date",
      subtag: "werdegang",
      sortable: true,
      required: true,
      validate: (value, entity) => {
        if (getAbschlussArt(entity) != ABSCHLUSSART.OHNE_ABSCHLUSS
          && _.isEmpty(value)) {
          return "Bitte geben Sie das Datum des Abschlusses ein";
        }
      },
    },
    {
      field: "pwg_ksbid",
      display: "Studiengang",
      sortable: true,
      subtag: "werdegang",
      type: "select",
      choices: lookup => _.map(_.filter(lookup, l => l.ksb_anzeige == true), l => ({ value: l.ksb_id, label: l.ksb_bez })),
      inject: ["k_studienbereichLookup"],
      selector: lookup => id => _.get(lookup, [id, "ksb_bez"], ""),
      required: true,
      validate: (value, entity) => {
        if (getAbschlussArt(entity) != ABSCHLUSSART.OHNE_ABSCHLUSS && getAbschlussArt(entity) != ABSCHLUSSART.HABILITIERT
          && _.isEmpty(value)) {
          return "Bitte geben Sie den Studiengang ein";
        }
      },
      hideFromDetail: entity => getAbschlussArt(entity) == ABSCHLUSSART.HABILITIERT,
      hideFromEditor: entity => getAbschlussArt(entity) == ABSCHLUSSART.HABILITIERT,
    },
    {
      field: "pwg_kfgid",
      display: "Fachgebiet",
      sortable: true,
      subtag: "werdegang",
      type: "select",
      choices: lookup => _.map(lookup, l => ({ value: l.kfg_id, label: l.kfg_bez })),
      inject: ["k_fachgebietLookup"],
      selector: lookup => id => _.get(lookup, [id, "kfg_bez"], ""),
      required: true,
      hideFromDetail: entity => getAbschlussArt(entity) != ABSCHLUSSART.HABILITIERT,
      hideFromEditor: entity => getAbschlussArt(entity) != ABSCHLUSSART.HABILITIERT,

      /*
       *validate: (value, entity) => {
       *  if (getAbschlussArt(entity) != ABSCHLUSSART.OHNE_ABSCHLUSS
       *    && _.isEmpty(value)) { return 'Bitte geben Sie den Studiengang ein' }
       *}
       */

    },
    {
      field: "pwg_ausbildungsgang",
      display: "Ausbildungsgang",
      subtag: "ausbildung",
    },
    {
      field: "pwg_ausbildungvon",
      display: "von",
      type: "month",
      subtag: "ausbildung",
      selector: (value, entity) => {
        const monat = (entity.pwg_ausbildungvonmonat || entity.awg_ausbildungvonmonat);
        const jahr = entity.pwg_ausbildungvonjahr || entity.awg_ausbildungvonjahr;

        if (!monat || !jahr) {
          return null;
        }
        return `${ deutscheMonatsnamen[monat - 1] } ${ jahr }`;
      },
      getValue: entity => {
        const datum = moment({
          year: entity.pwg_ausbildungvonjahr, month: entity.pwg_ausbildungvonmonat - 1,
        });
        return datum.isValid() ? datum : null;
      },
    },
    {
      field: "pwg_ausbildungbis",
      display: "bis",
      type: "month",
      subtag: "ausbildung",
      selector: (value, entity) => {
        const monat = (entity.pwg_ausbildungbismonat || entity.awg_ausbildungbismonat);
        const jahr = entity.pwg_ausbildungbisjahr || entity.awg_ausbildungbisjahr;
        if (!monat || !jahr) {
          return null;
        }
        return `${ deutscheMonatsnamen[monat - 1] } ${ jahr }`;
      },
      getValue: entity => {
        const datum = moment({
          year: entity.pwg_ausbildungbisjahr, month: entity.pwg_ausbildungbismonat - 1,
        });
        return datum.isValid() ? datum : null;
      },
    },
    {
      field: "pwg_kbaid",
      display: "Ausbildungsabschluss",
      lookup: "k_astat_job_graduation.select.kba_id.kba_bez",
      subtag: "ausbildung",
    },
    {
      field: "pwg_abschlussdatum",
      display: "Abschlussdatum",
      type: "date",
      subtag: "ausbildung",
    },
    {
      field: "pwg_ausbildungsbetrieb",
      display: "Ausbildungsbetrieb",
      subtag: "ausbildung",
    },
  ],
};
schema.table = setupLookups(schema);

const desc = {
  id: "pwg_id",
  name: "werdegang",
  endpoint: "/api/shkperson/werdegang",
  schema,
  breadcrumbs: {
    list: "Werdegang",
    detail: ba => ba.ba_kbez,
  },
};

const personwerdegangApi = api(desc);
export default personwerdegangApi;

export function beforeSaveWerdegang(entity) {
  if (getAbschlussArt(entity) == ABSCHLUSSART.HABILITIERT && !entity.pwg_kfgid && !entity.awg_kfgid) {
    const e = Error();
    e.response = {
      nachricht: "Bitte geben Sie ein Fachgebiet an.",
      details: [],
    };
    throw e;
  }
}

export function beforeSaveAusbildung(entity, edits) {
  // monat und jahr aus datum extrahieren
  if (edits.pwg_ausbildungvon) {
    edits.pwg_ausbildungvonmonat = moment(edits.pwg_ausbildungvon).month() + 1;
    edits.pwg_ausbildungvonjahr = moment(edits.pwg_ausbildungvon).year();
  } else if (edits.pwg_ausbildungvon === null) {
    edits.pwg_ausbildungvonmonat = null;
    edits.pwg_ausbildungvonjahr = null;
  }
  if (edits.pwg_ausbildungbis) {
    edits.pwg_ausbildungbismonat = moment(edits.pwg_ausbildungbis).month() + 1;
    edits.pwg_ausbildungbisjahr = moment(edits.pwg_ausbildungbis).year();
  } else if (edits.pwg_ausbildungbis === null) {
    edits.pwg_ausbildungbismonat = null;
    edits.pwg_ausbildungbisjahr = null;
  }
}
